// contexts/dpop-auth-context.tsx
"use client";

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { SignJWT } from "jose";

import { setGlobalDPoPClearSession } from "@/lib/auth-client";
import {
  clearDPoPKeyPair,
  generateAndStoreDPoPKeyPair,
  getOrCreateDPoPKeyPair,
  loadDPoPKeyPair,
} from "@/lib/dpop-keystore";

export interface DPoPAuthContextType {
  sessionKey: {
    privateKey: CryptoKey | null;
    publicKey: CryptoKey | null;
    publicKeyJWK: any | null;
  };
  accessToken: string | null;
  generateSessionKey: () => Promise<void>;
  setSessionKey: (key: {
    privateKey: CryptoKey;
    publicKey: CryptoKey;
    publicKeyJWK: any;
  }) => void;
  createDPoPProof: (htm: string, htu: string) => Promise<string>;
  setAccessToken: (token: string) => void;
  clearSession: () => void;
}

const DPoPAuthContext = createContext<DPoPAuthContextType | undefined>(
  undefined,
);

export function DPoPAuthProvider({ children }: { children: ReactNode }) {
  const [sessionKey, setSessionKey] = useState<{
    privateKey: CryptoKey | null;
    publicKey: CryptoKey | null;
    publicKeyJWK: any | null;
  }>({
    privateKey: null,
    publicKey: null,
    publicKeyJWK: null,
  });

  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Load existing keypair from IndexedDB on mount (private key stays non-extractable)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const load = async () => {
      try {
        const pair = await getOrCreateDPoPKeyPair();
        setSessionKey({
          privateKey: pair.privateKey,
          publicKey: pair.publicKey,
          publicKeyJWK: pair.publicKeyJWK,
        });
        // Access token lives in memory only — no localStorage read on mount.
      } catch {
        // Non-fatal — key will be generated on first sign-in
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateSessionKey = useCallback(async () => {
    const pair = await generateAndStoreDPoPKeyPair();
    setSessionKey({
      privateKey: pair.privateKey,
      publicKey: pair.publicKey,
      publicKeyJWK: pair.publicKeyJWK,
    });
  }, []);

  const createDPoPProof = useCallback(
    async (htm: string, htu: string): Promise<string> => {
      if (!sessionKey.privateKey || !sessionKey.publicKeyJWK) {
        throw new Error("No session key available");
      }

      try {
        // Debug: Calculate and log the JWK thumbprint for the proof
        try {
          const { calculateJwkThumbprint } = await import("jose");
          const thumbprint = await calculateJwkThumbprint(
            sessionKey.publicKeyJWK,
          );
          console.log("Creating DPoP proof with JWK thumbprint:", thumbprint);
        } catch (e) {
          console.error("Failed to calculate thumbprint for proof:", e);
        }

        const dpopProof = await new SignJWT({
          htm,
          htu,
          iat: Math.floor(Date.now() / 1000),
          jti: crypto.randomUUID(),
        })
          .setProtectedHeader({
            alg: "ES256",
            typ: "dpop+jwt",
            jwk: sessionKey.publicKeyJWK,
          })
          .sign(sessionKey.privateKey);

        return dpopProof;
      } catch (error) {
        console.error("Error creating DPoP proof:", error);
        throw error;
      }
    },
    [sessionKey],
  );

  // Callers must have already persisted via generateAndStoreDPoPKeyPair() — this only updates React state.
  const setSessionKeyDirect = useCallback(
    (key: {
      privateKey: CryptoKey;
      publicKey: CryptoKey;
      publicKeyJWK: any;
    }) => {
      setSessionKey(key);
    },
    [],
  );

  const setAccessTokenPersist = useCallback((token: string) => {
    // Token stays in memory only — writing to localStorage exposes it to XSS.
    setAccessToken(token);
  }, []);

  const clearSession = useCallback(() => {
    setSessionKey({ privateKey: null, publicKey: null, publicKeyJWK: null });
    setAccessToken(null);

    if (typeof window !== "undefined") {
      // Clear IndexedDB (private key)
      clearDPoPKeyPair().catch(() => {});

      // Clear all localStorage DPoP remnants
      const lsKeys = [
        "dpop_access_token",
        "dpop_private_key",
        "dpop_public_key_jwk",
        "dpop_key_thumbprint",
        "dpop-session",
        "dpop-key-pair",
        "dpop-thumbprint",
        "dpopKeyPair",
        "dpopAccessToken",
        "dpop_session",
        "dpop_keypair",
        "dpop_token",
        "dpop-token",
        "dpop_jwt",
        "dpop-jwt",
      ];
      lsKeys.forEach((k) => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      });
    }
  }, []);

  // Register the clearSession function globally for signOut
  useEffect(() => {
    setGlobalDPoPClearSession(clearSession);
  }, [clearSession]);

  return (
    <DPoPAuthContext.Provider
      value={{
        sessionKey,
        accessToken,
        generateSessionKey,
        setSessionKey: setSessionKeyDirect,
        createDPoPProof,
        setAccessToken: setAccessTokenPersist,
        clearSession,
      }}
    >
      {children}
    </DPoPAuthContext.Provider>
  );
}

export function useDPoPAuth() {
  const context = useContext(DPoPAuthContext);
  if (!context) {
    throw new Error("useDPoPAuth must be used within a DPoPAuthProvider");
  }
  return context;
}
